import { ChangeEvent, useEffect, useState } from 'react';
import { Input } from '../../../components/Form/Input';
import { addDiagram, resetDiagramPagination } from '../store/dashboardActions';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/authStore';
import { Description, Field, Label, Switch } from '@headlessui/react';
import UserComboBox from './UserComboBox';
import MemberList from './MemberList';
import { DiagramFormData } from './types';
import { DiagramModel, DiagramUserRoles } from '@diagram/model/diagram.model';
import { useModalStore } from '../../../shared/store/uiStore';
import { getDiagramStore } from '@diagram/store/diagram.store';
interface Props {
  diagramData?: DiagramFormData;
  id?: string;
}

const DiagramForm = ({ diagramData, id }: Props) => {
  const user = useAuthStore((state) => state.auth);
  const state = getDiagramStore();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<DiagramFormData>({
    name: '',
    description: '',
    isPublic: true,
    users: [],
  });
  const { closeModal } = useModalStore((state) => state);

  useEffect(() => {
    if (diagramData) {
      setFormData({
        name: diagramData.name,
        description: diagramData.description,
        isPublic: diagramData.isPublic,
        users: diagramData.users,
      });
    }
  }, [diagramData]);

  if (!user) return null;

  const handleChange = (
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    const diagramUsers = formData.users.reduce<
      Record<string, DiagramUserRoles>
    >((acc, user) => {
      acc[user.uid] = user.role;
      return acc;
    }, {});

    try {
      if (!id) {
        const newDiagramID = await addDiagram({
          name: formData.name,
          description: formData.description,
          access: formData.isPublic ? 'public' : 'private',
          users: {
            ...diagramUsers,
            [user.uid]: 'author',
          },
        });
        resetDiagramPagination();
        navigate(`v1/diagram/${newDiagramID}`);
      } else {
        const data = {
          name: formData.name,
          description: formData.description,
          access: formData.isPublic ? 'public' : 'private',
          users: { ...diagramUsers },
          id,
        } as DiagramModel;

        state.getState().meta.updateData(data);
      }
      closeModal();
    } catch (error) {
      console.error('Error creating diagram:', error);
    }
  };
  return (
    <form
      className="flex flex-col gap-4 mx-auto justify-center"
      id="diagram-form1"
      onSubmit={handleSubmit}
    >
      <Field className="flex items-center justify-between">
        <span className="flex grow flex-col mr-2">
          <Label
            as="span"
            passive
            className="text-sm/6 font-medium text-gray-900"
          >
            Make this diagram public
          </Label>
          <Description as="span" className="text-sm text-gray-500">
            Anybody in the organization can view this diagram.
          </Description>
        </span>
        <Switch
          checked={formData.isPublic}
          onChange={(checked) =>
            setFormData((prev) => ({ ...prev, isPublic: checked }))
          }
          className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-hidden data-checked:bg-primary"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-checked:translate-x-5"
          />
        </Switch>
      </Field>
      <div className="flex gap-4 w-full flex-col">
        <Input
          name="name"
          value={formData.name}
          onChange={handleChange}
          label="Diagram Name"
          placeholder="Diagram Name"
          className="w-full"
        />
        <div>
          <label
            htmlFor="description"
            className="block text-sm/6 font-medium text-gray-900"
          >
            Description
          </label>
          <div className="mt-2">
            <textarea
              id="description"
              name="description"
              rows={4}
              className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
              value={formData.description}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
      <div className="w-full">
        <UserComboBox setFormData={setFormData} users={formData.users} />
        <MemberList users={formData.users} setFormData={setFormData} />
      </div>
    </form>
  );
};
export default DiagramForm;
